/* eslint-disable @typescript-eslint/naming-convention */
import { Sites } from '@ps/platform-message-framework/common/Sites';
import { FeatureFlag } from '@ps/web-core-enums';
import { useFeature } from '@ps/web-core-store';
import type { RegulatoryHeader, SiteHeader } from '@ps/web-martech-cms-types';
import type {
  MainNavigationBlock,
  SecondaryNavigationBlock,
  SupportNavigationBlock
} from '@ps/web-martech-cms-types/SiteHeader';
import {
  TrackingCategory,
  TrackingContext,
  buttonClick as trackButtonClick
} from '@ps/web-shared-analytics';
import { Link } from '@ps/web-shared-components';
import classNames from 'clsx';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router';

import { CMSNavigation, CMSReferences } from '@swf/cms';
import { Vary } from '@swf/components/ResponseHeaders/ResponseHeaders';
import { Header } from '@swf/core/enums';
import { useRefs } from '@swf/core/providers/RefsProvider';
import { select } from '@swf/core/store';

import useCustomHeader from '../../hooks/useCustomHeader';
import useSeoMenu from '../../hooks/useSeoMenu';
import useStickyTopHeaderClassName from '../../hooks/useStickyTopHeaderClassName';
import { filterMainNavItems, filterSecondaryNavItems } from '../../utils/utils';

import styles from './CMSHeader.scss';
import CMSHeaderV2 from './CMSHeaderV2';
import CMSHeaderV3 from './CMSHeaderV3';
import AppDownloadBanner from './components/AppDownloadBanner/AppDownloadBanner';
import BrandIconButton from './components/BrandIcon/BrandIconButton';
import BrandIconMobile from './components/BrandIcon/BrandIconMobile';
import BrandLinks from './components/BrandLinks/BrandLinks';
import CMSHeaderMenu from './components/CMSHeaderMenu/CMSHeaderMenu';
import LoginMenu from './components/LoginMenu/LoginMenu';
import RamAccount from './components/RamAccount/RamAccount';
import SelfExclusionTooltip from './components/SelfExclusionTooltip/SelfExclusionTooltip';
import Sidebar from './components/Sidebar/Sidebar';
import TopHeader from './components/TopHeader/TopHeader';

function ensureLanguagesOnFirstPlace(navItems: SupportNavigationBlock[]) {
  return navItems.sort((a, b) => Number(b.icon === 'Languages') - Number(a.icon === 'Languages'));
}

const CMSHeader: React.FC<SiteHeader> = (props) => {
  const {
    brand_icon,
    brand_icon_url,
    layout = 'Real Money',
    main_navigation,
    secondary_navigation,
    support_navigation,
    regulatory_header
  } = props;
  const headerRef = useRef<HTMLDivElement>(null);
  const topHeaderRef = useRef<HTMLDivElement>(null);
  const { removeRef, setRefs } = useRefs();
  const [menuOpen, setMenuOpen] = useState(false);
  const toggleMenu = useCallback(() => {
    setMenuOpen((prevMenuOpen) => !prevMenuOpen);
    !menuOpen && trackButtonClick({ buttonName: 'Hamburger Menu' });
  }, [menuOpen]);
  const useRam1 = useFeature(FeatureFlag.UseRam1);
  const casinoNonAuth = useFeature(FeatureFlag.CasinoNonAuth);
  const sportsNonAuth = useFeature(FeatureFlag.SportsNonAuth);
  const site = useSelector(select.cms.getSiteId);
  const isEmbedded = useSelector(select.device.isEmbedded);
  const disableRam = useFeature(FeatureFlag.DisableRam);
  const isMobile = useSelector(select.device.isMobile);
  const embeddedRoute = useSelector(select.location.isEmbeddedRoute);
  const showWebContent = !isEmbedded && !embeddedRoute;
  const { seoMenu, seoDepth } = useSeoMenu(layout);
  const DEPanic = useFeature(FeatureFlag.DEPanic);
  const isAuthenticated = useSelector(select.session.isAuthenticated);
  const isSportsSelfExcluded = useSelector(select.account.isSportsSelfExcluded);
  const {
    lightHeader,
    prospectHeader,
    clickableLogoUrl,
    headerLink,
    showLocalesDropdown,
    headerV2,
    headerV3
  } = useCustomHeader();
  const { styles: stickyTopHeaderClassName, isStickyHeader } =
    useStickyTopHeaderClassName('header');
  const { pathname } = useLocation();
  const isOnPokerVertical = pathname.startsWith('/poker/');
  const brandIconUrl = clickableLogoUrl || brand_icon_url;
  const showSEONav = seoMenu && showWebContent && !lightHeader && !prospectHeader;
  const showDePanicButton = DEPanic && isAuthenticated;
  const orderedSupportNav = ensureLanguagesOnFirstPlace(support_navigation);
  const showCustomLightHeader = !lightHeader || (lightHeader && showLocalesDropdown);
  const filteredMainNavigation: MainNavigationBlock[] = filterMainNavItems(main_navigation, {
    casinoNonAuth,
    sportsNonAuth
  });
  const filteredSecondaryNavigation: SecondaryNavigationBlock[] = filterSecondaryNavItems(
    secondary_navigation,
    pathname
  );

  const showSecondaryNav =
    !isSportsSelfExcluded &&
    !lightHeader &&
    !prospectHeader &&
    filteredSecondaryNavigation?.length > 0;

  useEffect(() => {
    setRefs('header', headerRef);
    setRefs('topHeader', topHeaderRef);

    return () => {
      removeRef('header');
      removeRef('topHeader');
    };
  }, [setRefs, removeRef]);

  useEffect(() => {
    if (menuOpen) {
      (document.documentElement as HTMLElement).scrollTop = 0;
    }
  }, [menuOpen]);

  if (headerV2 && isOnPokerVertical) {
    return <CMSHeaderV2 {...props} />;
  }

  if (headerV3 && isOnPokerVertical) {
    return <CMSHeaderV3 {...props} />;
  }

  return layout === 'Hidden' ? null : (
    <TrackingContext.Provider
      value={{
        category: TrackingCategory.Stars,
        widget: { widgetName: 'CMS Header Widget', widgetId: 'cms-header-widget' }
      }}
    >
      {showWebContent && <AppDownloadBanner />}
      <header
        id="CMSheader"
        ref={headerRef}
        className={classNames(styles.header, stickyTopHeaderClassName, {
          [styles.desktopHeader]: !isMobile
        })}
      >
        <Vary by={[Header.Auth]} />
        <div
          id="sticky-top-container"
          className={classNames({
            [styles.stickyTopContainer]: isStickyHeader,
            [styles.yellowBackground]: site === Sites.FRA
          })}
        >
          <TopHeader
            ref={topHeaderRef}
            regulatory_header={regulatory_header as unknown as RegulatoryHeader[]}
          />
        </div>
        {!lightHeader && (
          <div
            className={classNames(styles.seoMenuContainer, styles.desktop, {
              [styles.showDesktop]: !isMobile
            })}
          >
            <div
              className={classNames(styles.seoMenu, {
                [styles.showDePanicButton]: showDePanicButton
              })}
            >
              <div>
                {showSEONav && <CMSNavigation isSeoMenu menu={seoMenu} maxDepth={seoDepth || 1} />}
              </div>
              <div className={styles.supportNav}>
                {showDePanicButton && (
                  <div className={styles.selfExclusion}>
                    <SelfExclusionTooltip />
                  </div>
                )}
                <CMSHeaderMenu items={orderedSupportNav} />
              </div>
            </div>
          </div>
        )}
        <div
          className={classNames({
            [styles.desktopRamActive]: useRam1 && !isMobile && !lightHeader
          })}
        >
          {!lightHeader && (
            <div className={classNames(styles.headerLink, { [styles.hideMobile]: !isMobile })}>
              {site !== Sites.NET && <BrandLinks main={filteredMainNavigation} />}
            </div>
          )}
          <div
            className={classNames(styles.menu, styles.headerMenuHeight, {
              [styles.mobileRamActive]: useRam1 && !lightHeader,
              [styles.desktopMenu]: !isMobile,
              [styles.secondaryNavigation]: showSecondaryNav,
              [styles.lightHeader]: lightHeader
            })}
          >
            <div className={styles.brandLinks}>
              <div
                className={classNames(styles.menuContent, styles.desktop, {
                  [styles.showDesktop]: !isMobile
                })}
              >
                <div
                  className={classNames(styles.menuContent, styles.desktop, {
                    [styles.showDesktop]: !isMobile
                  })}
                >
                  <BrandIconButton brandIcon={brand_icon} brandIconUrl={brandIconUrl} />
                </div>
                {!lightHeader && <BrandLinks main={filteredMainNavigation} />}
              </div>
              <span
                className={classNames(styles.menuButton, styles.headerLink, {
                  [styles.hideMenuButton]: !isMobile,
                  [styles.lightHeaderMenuButton]: lightHeader,
                  [styles.customLightHeaderMenuButton]: showLocalesDropdown
                })}
                onClick={toggleMenu}
              >
                {showCustomLightHeader && (
                  <div data-testid="burger-menu">
                    <span className={styles.burgerSlice} />
                    <span className={styles.burgerSlice} />
                    <span className={styles.burgerSlice} />
                  </div>
                )}
                {brandIconUrl && lightHeader && !showLocalesDropdown ? (
                  <Link href={brandIconUrl} track trackText="Brand Icon">
                    <BrandIconMobile brandIcon={brand_icon} />
                  </Link>
                ) : (
                  <BrandIconMobile brandIcon={brand_icon} />
                )}
              </span>
              {showSecondaryNav && (
                <div
                  className={classNames(styles.menuContent, styles.headerLink, {
                    [styles.desktop]: layout !== 'Play Money',
                    [styles.showDesktop]: layout !== 'Play Money' && !isMobile
                  })}
                >
                  <CMSHeaderMenu
                    items={filteredSecondaryNavigation}
                    className={classNames(styles.cmsMenu, {
                      [styles.noGap]: layout === 'Play Money'
                    })}
                    layout={layout}
                    secondaryMenu
                  />
                </div>
              )}
            </div>
            {layout === 'Real Money' && !lightHeader && (
              <div>
                {!disableRam && (
                  <div className={styles.accountLinks}>
                    {useRam1 ? <RamAccount /> : <LoginMenu />}
                  </div>
                )}
              </div>
            )}
            {((lightHeader && headerLink) || (showLocalesDropdown && !isMobile)) && (
              <div className={styles.lightHeaderContainer}>
                {lightHeader && headerLink && <CMSReferences references={headerLink} />}
                {showLocalesDropdown && !isMobile && (
                  <div className={styles.localesDropdown}>
                    <CMSHeaderMenu items={support_navigation} className={styles.cmsMenu} />
                  </div>
                )}
              </div>
            )}
          </div>
          {showCustomLightHeader && (
            <div className={classNames(styles.headerLink, { [styles.hideMobile]: !isMobile })}>
              <Sidebar
                menuOpen={menuOpen}
                toggleMenu={toggleMenu}
                brandIcon={brand_icon}
                brandIconUrl={brandIconUrl}
                main={filteredMainNavigation}
                primary={support_navigation}
                secondary={prospectHeader === false ? filteredSecondaryNavigation : []}
                layout={layout}
              />
            </div>
          )}
        </div>
      </header>
    </TrackingContext.Provider>
  );
};

export default CMSHeader;
